import { cn } from '@/lib/utils'
import { KEEP_SCROLL_STATE } from '@/utils/hooks/use-scroll-restoration'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

export type SalaryWorkTimeTab =
  | 'general'
  | 'salaryCodes'
  | 'overtime'
  | 'pause'
  | 'addons'

/** The open tab, kept in the url so a reload or a shared link lands on it. */
export const useSalaryWorkTimeTab = (tabs: SalaryWorkTimeTab[]) => {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const tab = tabs.find(candidate => candidate === requested) ?? 'general'

  const setTab = useCallback(
    (next: SalaryWorkTimeTab) =>
      setParams(
        previous => {
          const nextParams = new URLSearchParams(previous)
          nextParams.set('tab', next)
          return nextParams
        },
        { replace: true, state: KEEP_SCROLL_STATE }
      ),
    [setParams]
  )

  return [tab, setTab] as const
}

export const SalaryWorkTimeTabs: React.FC<{
  tabs: SalaryWorkTimeTab[]
  active: SalaryWorkTimeTab
  invalid: Partial<Record<SalaryWorkTimeTab, boolean>>
  onChange(tab: SalaryWorkTimeTab): void
}> = ({ tabs, active, invalid, onChange }) => {
  const labels = useTranslate({
    general: 'common.general',
    salaryCodes: 'common.salary-codes',
    overtime: 'common.overtime',
    pause: 'common.pause',
    addons: 'salary.addons',
  })

  return (
    <nav className="flex gap-6 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--color-stone-200)] [scrollbar-width:none]">
      {tabs.map(tab => (
        <button
          key={tab}
          type="button"
          aria-current={tab === active ? 'page' : undefined}
          className={cn(
            'flex shrink-0 items-center gap-1.5 border-b-2 border-transparent pb-2 text-sm font-semibold whitespace-nowrap text-stone-500 hover:text-stone-900',
            tab === active && 'border-stone-900 text-stone-900'
          )}
          onClick={() => onChange(tab)}
        >
          {labels[tab]}
          {invalid[tab] && (
            <span className="size-1.5 rounded-full bg-red-500" aria-hidden />
          )}
        </button>
      ))}
    </nav>
  )
}
